/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Heading, Types } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import {
  chefHighlight,
  outletPageUrl,
  signatureItems,
  TRACKING_CONSTANT,
} from '../../modules/utils/constants/constants';
import { getDirection, getSubDirectoryLocale, useGetBreakpoint } from '../../modules/utils/helpers';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Glide from '@glidejs/glide';
import { CarouselContainer } from '../OutletDetails/PremiumMenuDetails/CarouselContainer/CarouselContainer';
import { StyledSignatureChefContainer } from './SignatureChefContainer.styles';
import { useLazyQuery } from '@apollo/client';
import { outletChefFeatureDetails, searchBestRestaurantsInCity } from '@marriott/mi-rnb-graphql';
import Cookies from 'js-cookie';
import { PageParamsContext } from '../../modules/context/PageContext';
import chefFeatureMock from './__mock__/chefFeatureMock.json';
import signatureItemsMock from './__mock__/signatureItemsMock.json';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const SignatureChefContainer = (props: any) => {
  const { currentLocale } = useContext(PageParamsContext);
  const subLocale = getSubDirectoryLocale(currentLocale || 'en-US');
  const pageContext = useContext(createContext<any>({}));
  const requestId = useMemo(() => {
    return pageContext.requestId ? pageContext.requestId : `${Date.now()}`;
  }, [pageContext]);
  const sessionID = Cookies.get('sessionID');
  const currentTimestamp = Date.now();
  const currentDateTimeStamp = new Date(currentTimestamp).getTime();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const glideRef = useRef<HTMLDivElement | any>(null);
  const [componentData, setComponentData] = useState<any>([]);
  const [signatureDishComponentData, setSignatureDishComponentData] = useState<any>([]);
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const outletId = props?.componentName === chefHighlight ? props?.outletIdsList[0]?.outletId : '';
  const propertyId = props?.componentName === chefHighlight ? props?.outletIdsList[0]?.propertyId : '';
  const { CITY_PAGE, SIGNATURE_DISHES, CHEF_HIGHLIGHT, INTERNAL_LINK } = TRACKING_CONSTANT;

  const [getChefHighlightData] = useLazyQuery(outletChefFeatureDetails);
  const [getSignatureDishData] = useLazyQuery(searchBestRestaurantsInCity);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (props?.isAuthorMode) {
      console.log('123 in authormode useEffect - signChef', props.isAuthorMode);
      if (props?.componentName === chefHighlight) setComponentData([chefFeatureMock.data]);
      else if (props?.componentName === signatureItems)
        setSignatureDishComponentData(
          signatureItemsMock?.data?.content?.restaurant?.signatureDishesAtRestaurantByOutletId?.data?.signatureDishes
        );
    }
  }, [props?.isAuthorMode]);

  useEffect(() => {
    if (outletId && propertyId && props?.componentName === chefHighlight && !props?.isAuthorMode) {
      console.log('123 in UXL useEffect - signChef', props.isAuthorMode);
      getChefHighlightData({
        fetchPolicy: 'network-only',
        ssr: false,
        errorPolicy: 'all',
        variables: { outletId: outletId, propertyId: propertyId, restaurantId: Number(outletId) },
        context: {
          headers: {
            'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
            'x-request-id': requestId,
            'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
            'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
            'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
          },
        },
        onCompleted: data => {
          if (data) {
            setComponentData([data]);
          } else {
            setComponentData([]);
          }
        },
        onError: () => {
          setComponentData([]);
        },
      });
    } else if (props?.city && props?.country && props?.componentName === signatureItems && !props?.isAuthorMode) {
      getSignatureDishData({
        fetchPolicy: 'network-only',
        ssr: false,
        errorPolicy: 'all',
        variables: { city: props?.city, countryIso: props?.country },
        context: {
          headers: {
            'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
            'x-request-id': requestId,
            'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
            'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
            'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
          },
        },
        onCompleted: data => {
          if (data?.content?.restaurant?.signatureDishesAtRestaurantsInCity?.signatureDishData?.edges) {
            // Flatten all signature dishes from all restaurants into a single array
            const allSignatureDishes =
              data.content.restaurant.signatureDishesAtRestaurantsInCity.signatureDishData.edges.flatMap((edge: any) =>
                edge.signatureDishes.map((dish: any) => ({
                  ...dish,
                  outletDetails: edge.outletDetails,
                }))
              );
            setSignatureDishComponentData(allSignatureDishes.slice(0, 10));
          } else {
            setSignatureDishComponentData([]);
          }
        },
        onError: () => {
          setSignatureDishComponentData([]);
        },
      });
    }
  }, [outletId, propertyId, props?.isAuthorMode]);

  const ArrowDisabler = useCallback(
    (Glide: any, Components: any) => {
      return {
        mount() {
          // Only in effect when rewinding is disabled
          // if (Glide.settings.rewind) {
          //   return;
          // }
          Glide.on(['mount.after', 'run'], () => {
            const component = document?.getElementById(`${props?.componentId}`);
            if (component) {
              component.style.visibility = 'visible';
            }

            // Filter out arrows_control
            for (const controlItem of Components.Controls.items) {
              if (controlItem.className !== 'glide__arrows') {
                continue;
              }

              // Set left arrow state
              const left = controlItem.querySelector('.glide__arrow--left');
              const direction = getDirection();
              if (left) {
                if (direction === 'rtl' && Glide.index === Components.Sizes.length - Glide.settings.perView) {
                  left.classList.add('.is-disabled'); // Disable on first slide
                } else if (direction === 'ltr' && Glide.index === 0) {
                  left.classList.add('.is-disabled'); // Disable on first slide
                } else {
                  left.classList.remove('.is-disabled'); // Enable on other slides
                }
              }

              // Set right arrow state
              const right = controlItem.querySelector('.glide__arrow--right');
              if (right) {
                if (direction === 'rtl' && Glide.index === 0) {
                  right.classList.add('.is-disabled'); // Disable on last slide
                } else if (direction === 'ltr' && Glide.index === Components.Sizes.length - Glide.settings.perView) {
                  right.classList.add('.is-disabled');
                } else {
                  right.classList.remove('.is-disabled'); // Disable on other slides
                }
              }
            }
          });
        },
      };
    },
    [props?.componentId]
  );

  useEffect(() => {
    const carouselRootSelector = `#${props?.componentId}`;
    const direction = getDirection();

    // Destroy existing Glide instance if it exists
    const existingElement = document?.querySelector(carouselRootSelector);
    if (existingElement && (existingElement as any)._glide) {
      (existingElement as any)._glide.destroy();
    }

    if (document?.querySelector(carouselRootSelector)) {
      try {
        if (props?.componentId) {
          const glideInstance = new Glide(carouselRootSelector, {
            type: 'slider',
            gap: 0,
            startAt: 0,
            focusAt: 'center',
            autoplay: false,
            bound: false,
            rewind: false,
            dragThreshold: false,
            direction: direction,
            perView: 1,
            peek: {
              before: 0,
              after: 0,
            },
            keyboard: false,
          }).mount({ ArrowDisabler });

          // Store the instance for cleanup
          (existingElement as any)._glide = glideInstance;
        }
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }

    // Cleanup function
    return () => {
      const element = document?.querySelector(carouselRootSelector);
      if (element && (element as any)._glide) {
        try {
          (element as any)._glide.destroy();
          delete (element as any)._glide;
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [props?.componentId, componentData, signatureDishComponentData, ArrowDisabler]);

  const generateOutletLink = (propertyId: string, propertyName: string, outletName: string, outletId: string) => {
    const cleanPropertyName = propertyName
      ?.replace(/[^A-Z0-9]+/gi, '-')
      ?.replace(/^-+|-+$/g, '')
      ?.toLowerCase();
    const cleanOutletName = outletName
      ?.replace(/[^A-Z0-9]+/gi, '-')
      ?.replace(/^-+|-+$/g, '')
      ?.toLowerCase();

    const primaryLinkUrl = `${propertyId}-${cleanPropertyName}/${outletId}-${cleanOutletName}`;
    return `${subLocale}${outletPageUrl}${primaryLinkUrl}.mi`;
  };

  const getChefPrimaryLink = () => {
    const propertyName =
      componentData[0]?.restaurantById?.properties?.edges[0]?.node?.property?.seoNickname || propertyId;
    const outletName = componentData[0]?.restaurantById?.basicInformation?.name;
    return generateOutletLink(propertyId, propertyName, outletName, outletId);
  };

  const getChildrenComponent = (data: any, index: number) => {
    if (props?.componentName === signatureItems) {
      const componentProps = {
        eyebrowText: props?.eyebrowText,
        imageData: data?.imageData,
        header: data?.name,
        description: data?.description,
        isMobileViewPort: isMobileViewPort,
        locationInfo: data?.outletDetails?.name,
        iconPath: props?.iconPath,
        primaryCtaType: props?.ctaType1,
        primaryCtaLabel: props?.ctaLinkText,
        primaryCtaLink: generateOutletLink(
          data?.outletDetails?.marshaCode?.toLowerCase(),
          data?.outletDetails?.propertyName,
          data?.outletDetails?.name,
          data?.outletDetails?.epicID
        ),
        primaryCtaOpenInNewTab: props?.openInNewTab,
        secondaryCtaType: props?.ctaType2,
        secondaryCtaLabel: props?.ctaLinkText2,
        secondaryCtaLink: data?.menuDetails?.guid
          ? `${generateOutletLink(
              data?.outletDetails?.marshaCode?.toLowerCase(),
              data?.outletDetails?.propertyName,
              data?.outletDetails?.name,
              data?.outletDetails?.epicID
            )}?menu=${data?.menuDetails?.guid}`
          : generateOutletLink(
              data?.outletDetails?.marshaCode?.toLowerCase(),
              data?.outletDetails?.propertyName,
              data?.outletDetails?.name,
              data?.outletDetails?.epicID
            ),
        secondaryCtaOpenInNewTab: props?.openInNewTab2,
        customCLickTrackDataLoc: `${CITY_PAGE}-${SIGNATURE_DISHES}`,
        customCLickTrackLinkType: `${INTERNAL_LINK}`,
        ...props,
      };
      return (
        <li key={index}>
          <CarouselContainer {...componentProps} />
        </li>
      );
    } else if (props?.componentName === chefHighlight) {
      const componentProps = {
        eyebrowText: props?.eyebrowText,
        imageData: componentData[0]?.content?.restaurant?.chefDetailsByOutletId?.chefDetails?.imageData,
        header: componentData[0]?.restaurantById?.chef?.name,
        description: componentData[0]?.restaurantById?.chef?.description,
        isMobileViewPort: isMobileViewPort,
        locationInfo: componentData[0]?.restaurantById?.basicInformation?.name,
        iconPath: props?.iconPath,
        primaryCtaType: props?.ctaType1,
        primaryCtaLabel: props?.ctaLinkText,
        primaryCtaLink: getChefPrimaryLink(),
        primaryCtaOpenInNewTab: props?.openInNewTab,
        customCLickTrackDataLoc: `${CITY_PAGE}-${CHEF_HIGHLIGHT}`,
        customCLickTrackLinkType: `${INTERNAL_LINK}`,
        ...props,
      };
      return <CarouselContainer {...componentProps} />;
    } else return null;
  };

  return (componentData && componentData.length > 0) ||
    (props?.componentName === signatureItems && signatureDishComponentData && signatureDishComponentData.length > 0) ? (
    <StyledSignatureChefContainer className={clsx(props?.styleclass, 'py-4 py-md-5')}>
      <div className="container">
        {props?.titleLabel && (
          <div className="text-center">
            <Heading
              titleText={props?.titleLabel}
              variation={Types.headingType.title}
              fontSize={Types.size.medium}
              customClass="mb-4 mb-md-5"
            />
          </div>
        )}

        <div
          id={props?.componentId}
          className="glide col-12 px-0 pt-0 pb-3"
          ref={glideRef}
          data-testid="gallery-carousal-div"
        >
          <div className="glide__track" data-glide-el="track">
            <ul className={clsx('glide__slides p-0')} id={`${props?.componentId}__slides`}>
              {props?.componentName === signatureItems
                ? signatureDishComponentData?.map((item: any, index: number) => getChildrenComponent(item, index))
                : componentData?.map((item: any, index: number) => getChildrenComponent(item, index))}
            </ul>
          </div>

          {((props?.componentName === signatureItems &&
            signatureDishComponentData &&
            signatureDishComponentData.length > 1) ||
            (props?.componentName !== signatureItems && componentData && componentData.length > 1)) && (
            <div className="controls_wrapper">
              <div className="controls d-flex align-items-center justify-content-center">
                <div className="carouselControl carouselControlType2">
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className={clsx('left-arrow', 'glide__arrow--left')}
                      id={`${props?.componentId}_left_arrow`}
                      data-glide-dir="<"
                      data-content={props?.previousButtonLabel}
                      aria-label={props?.previousButtonLabel}
                    >
                      <span className="icon-arrow-left" data-id="left"></span>
                    </button>
                  </div>

                  <div data-glide-el="controls[nav]">
                    {props?.componentName === signatureItems
                      ? signatureDishComponentData?.map((_: any, index: number) => {
                          return <button className="" data-glide-dir={index} key={index}></button>;
                        })
                      : componentData?.map((_: any, index: number) => {
                          return <button className="" data-glide-dir={index} key={index}></button>;
                        })}
                  </div>

                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className={clsx('right-arrow', 'glide__arrow--right')}
                      id={`${props?.componentId}_right_arrow`}
                      data-glide-dir=">"
                      data-content={props?.nextButtonLabel}
                      aria-label={props?.nextButtonLabel}
                    >
                      <span className="icon-arrow-right" data-id="right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </StyledSignatureChefContainer>
  ) : null;
};

export const SignatureChefContainerConfig = {
  emptyLabel: 'SignatureChefContainer',
  isEmpty: false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/signatureandchefcomponent`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SignatureChefContainerEditable = (props: any) => (
  <EditableComponent config={SignatureChefContainerConfig} {...props}>
    <SignatureChefContainer {...props} />
  </EditableComponent>
);
